function parseAnnouncementEndDate(dateValue, timezoneValue) {
  const date = String(dateValue || '').split(',');

  if (date.length < 4) {
    return null;
  }

  const year = Number.parseInt(date[0], 10);
  const month = Number.parseInt(date[1], 10);
  const day = Number.parseInt(date[2], 10);
  const hour = Number.parseInt(date[3], 10);
  const minute = Number.parseInt(date[4] || '0', 10);

  if (![year, month, day, hour, minute].every(Number.isFinite)) {
    return null;
  }

  const count = new Date(year, month - 1, day, hour, minute);
  const gmt = Number.parseInt(timezoneValue, 10);

  if (Number.isFinite(gmt)) {
    const timezone = count.getTimezoneOffset() / -60;

    if (timezone !== gmt) {
      count.setHours(count.getHours() + (timezone - gmt));
    }
  }

  return count;
}

function padAnnouncementUnit(value) {
  return String(Math.max(value, 0)).padStart(2, '0');
}

if (typeof customElements.get('announcement-countdown') === 'undefined') {
  class AnnouncementCountdown extends HTMLElement {
    connectedCallback() {
      this.valueEl = this.querySelector('.announcement__countdown-value');
      this.endDate = parseAnnouncementEndDate(this.dataset.date, this.dataset.timezone);
      this.intervalId = null;

      if (!this.endDate || !this.valueEl) {
        return;
      }

      this.tick = this.tick.bind(this);
      this.tick();
      this.intervalId = window.setInterval(this.tick, 1000);
    }

    disconnectedCallback() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    tick() {
      const distance = this.endDate.getTime() - Date.now();

      if (distance <= 0) {
        this.render(0, 0, 0, 0);

        if (this.intervalId) {
          window.clearInterval(this.intervalId);
          this.intervalId = null;
        }

        return;
      }

      this.render(
        Math.floor(distance / (1000 * 60 * 60 * 24)),
        Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        Math.floor((distance % (1000 * 60)) / 1000)
      );
    }

    render(days, hours, minutes, seconds) {
      this.valueEl.textContent = [
        padAnnouncementUnit(days),
        padAnnouncementUnit(hours),
        padAnnouncementUnit(minutes),
        padAnnouncementUnit(seconds)
      ].join(':');
    }
  }

  customElements.define('announcement-countdown', AnnouncementCountdown);
}

if (typeof customElements.get('announcement-bar') === 'undefined') {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();

      this.initialized = false;
      this.autoplayTimer = null;
    }

    connectedCallback() {
      if (!this.initialized) {
        this.initialize();
      }
    }

    disconnectedCallback() {
      if (!this.initialized) {
        return;
      }

      this.stopAutoplay();
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.prevButton?.removeEventListener('click', this.handlePrevClick);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.initialized = false;
    }

    initialize() {
      this.slider = this.querySelector('[data-js-slider]');
      this.content = Array.from(this.querySelectorAll('.announcement'));
      this.nextButton = this.querySelector('.announcement-bar__content-nav--right');
      this.prevButton = this.querySelector('.announcement-bar__content-nav--left');
      this.index = 0;
      this.length = this.content.length;
      this.autoSlide = this.dataset.autoSlide === 'true';
      this.fadeTransition = this.dataset.fadeTransition === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const intervalSeconds = Number.parseFloat(this.dataset.slideInterval);
      this.slideInterval = Math.max(Number.isFinite(intervalSeconds) ? intervalSeconds : 5, 1) * 1000;

      this.handleNextClick = () => this.handleManualChange('next');
      this.handlePrevClick = () => this.handleManualChange('prev');
      this.handleMouseEnter = () => this.stopAutoplay();
      this.handleMouseLeave = () => this.startAutoplay();
      this.handleFocusIn = () => this.stopAutoplay();
      this.handleFocusOut = () => this.startAutoplay();
      this.handleVisibilityChange = () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      };

      this.initialized = true;

      if (!this.slider || this.length === 0) {
        return;
      }

      this.updateSlideState();
      this.scheduleHides();

      if (this.length < 2) {
        return;
      }

      this.nextButton?.addEventListener('click', this.handleNextClick);
      this.prevButton?.addEventListener('click', this.handlePrevClick);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.startAutoplay();
    }

    handleManualChange(direction) {
      this.stopAutoplay();
      this.changeSlide(direction);
      this.startAutoplay();
    }

    changeSlide(direction) {
      let nextIndex = this.index;

      if (direction === 'next') {
        nextIndex += 1;
      } else if (direction === 'prev') {
        nextIndex -= 1;
      }

      this.showSlide(Math.min(Math.max(nextIndex, 0), this.length - 1));
    }

    showSlide(index) {
      if (index < 0 || index >= this.length || index === this.index) {
        return;
      }

      this.index = index;
      this.updateSlideState();

      if (!this.fadeTransition) {
        this.slider.scrollTo({
          top: 0,
          left: this.content[this.index].offsetLeft,
          behavior: this.reducedMotion ? 'auto' : 'smooth'
        });
      }
    }

    updateSlideState() {
      this.content.forEach((slide, index) => {
        const isActive = index === this.index;

        slide.classList.toggle('announcement--active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        slide.querySelectorAll('a').forEach(link => {
          if (isActive) {
            link.removeAttribute('tabindex');
          } else {
            link.setAttribute('tabindex', '-1');
          }
        });
      });

      this.updateNavigation();
    }

    updateNavigation() {
      this.setNavigationState(this.prevButton, this.index === 0);
      this.setNavigationState(this.nextButton, this.index === this.length - 1);
    }

    setNavigationState(button, disabled) {
      if (!button) {
        return;
      }

      button.disabled = disabled;
      button.classList.toggle('announcement-bar__content-nav--disabled', disabled);
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    }

    startAutoplay() {
      if (
        !this.autoSlide ||
        this.length < 2 ||
        this.autoplayTimer ||
        this.reducedMotion ||
        document.hidden ||
        this.matches(':hover') ||
        this.contains(document.activeElement)
      ) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.showSlide((this.index + 1) % this.length);
      }, this.slideInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    scheduleHides() {
      if (window.Shopify?.designMode) {
        return;
      }

      this.content.forEach(slide => {
        if (!slide.dataset.hideAt) {
          return;
        }

        const endDate = parseAnnouncementEndDate(slide.dataset.hideAt, slide.dataset.hideTimezone);

        if (!endDate) {
          return;
        }

        this.scheduleHide(slide, endDate);
      });
    }

    scheduleHide(slide, endDate) {
      const remaining = endDate.getTime() - Date.now();

      if (remaining <= 0) {
        this.hideSlide(slide);
        return;
      }

      window.setTimeout(() => {
        this.scheduleHide(slide, endDate);
      }, Math.min(remaining, 2147483647));
    }

    hideSlide(slide) {
      if (!slide || slide.hidden) {
        return;
      }

      slide.hidden = true;
      slide.setAttribute('aria-hidden', 'true');
      slide.classList.remove('announcement--active');

      const blockId = slide.dataset.blockId;

      if (blockId) {
        document.querySelectorAll(`.fixed-announcement-bar .announcement[data-block-id="${blockId}"]`).forEach(clonedSlide => {
          clonedSlide.hidden = true;
          clonedSlide.setAttribute('aria-hidden', 'true');
        });
      }

      this.refreshAfterHide();
    }

    refreshAfterHide() {
      this.content = Array.from(this.querySelectorAll('.announcement:not([hidden])'));
      this.length = this.content.length;

      if (this.length === 0) {
        this.hideBar();
        return;
      }

      if (this.index >= this.length) {
        this.index = 0;
      }

      this.updateSlideState();

      if (this.length < 2) {
        this.stopAutoplay();
        this.prevButton?.setAttribute('hidden', '');
        this.nextButton?.setAttribute('hidden', '');
      }
    }

    hideBar() {
      this.hidden = true;
      this.style.display = 'none';
      document.querySelectorAll('.fixed-announcement-bar').forEach(bar => {
        bar.hidden = true;
        bar.style.display = 'none';
      });
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}